import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { ApiError } from '@/lib/api/client';

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Auth and validation failures never succeed on a retry.
          if (error instanceof ApiError && error.status < 500 && error.status !== 429) return false;
          // A rate limit is worth exactly one patient retry; hammering it is
          // what caused the limit in the first place.
          if (error instanceof ApiError && error.status === 429) return failureCount < 1;
          return failureCount < 2;
        },
        retryDelay: (attempt, error) =>
          error instanceof ApiError && error.status === 429
            ? 8_000
            : Math.min(1_000 * 2 ** attempt, 15_000),
      },
      mutations: {
        onError: (error) => {
          const message =
            error instanceof ApiError ? error.message : 'Something went wrong. Please try again.';
          toast.error(message);
        },
      },
    },
  });
}

export function QueryProvider({ children }: { children: React.ReactNode }) {
  const [client] = useState(createQueryClient);
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
